import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import { getCreateTransactionBalance } from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import globalText from "../../../../utils/globalText";
import UniversalRefreshToast from "../../../_utils/UniversalRefreshToast";
import Loader from "../../../Loader/Index";
import useServiceChecker from "../../../ServiceChecker";
import CommonAlertCard from "../../CommonAlertCard";
import CommonBankLogo from "../../CommonBankLogo";
import IconTearDropButton from "../../CommonButtons/IconTearDropButton";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import CommonToast from "../../CommonToast";
import CardActionableButton from "../../CommonViewAllCard/CardSubComponent/CardActionableButton";
import { formatAccountNumber } from "../../FormatAccountNumber";
import SVGComponent from "../../SVGComponent";
import TickerAnimation from "../../TickerAnimation";
import CommonAccountModal from "./CommonAccountModal";
// Suggestions of props for component while using the components -------------------------------------
/**
 * @param {String} props.type - passs type like "source" or "destination"
 * @param {String} props.flow - If type is "destination" or "internal", then pass "beneficiary" or "internal"
 * @param {String} props.page - If you want this component on confirm page then, pass "confirm"
 * @param {Boolean} props.modalOpen - Is modal open or not
 * @param {Object} props.setModalOpen - Setter of modal state ( true or false )
 * @param {Object} props.txnPayLoad - txnPayload array
 * @param {Object} props.setTxnPayLoad - Setter of txnPayload
 * @param {Boolean} props.shouldShowAssociatedAccounts -  If you are using type = "destination", then pass state of showing associted accounts found
 * @param {Function} props.handleOnAccountSelection - If you are using type = "destination", then pass handle function of Associate account found
 * @returns {JSX.Element}
 */

const SourceDestinationAccountDetail = ({
  type,
  flow,
  page,
  modalOpen,
  setModalOpen,
  txnPayLoad,
  setTxnPayLoad,
  shouldShowAssociatedAccounts,
  handleOnAccountSelection,
  isDisabled,
  isEditDisabled,
  isReimbursement,
  activeType,
  setActiveType,
  setRemPan,
  setRemAccount,
  setRemIFSC,
  remIFSC,
  remAccount,
  remPan,
  TypeRTGS,
  currentAccountDetails,
  isFieldforce,
  flowType,
  accountHeader,
  accountSubHeader,
}) => {
  const shouldBeRedacted = (price) => {
    return (
      price === "xxxx.xx" ||
      isNaN(+price) ||
      price === null ||
      price === undefined
    );
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toastIdRef = useRef(null);
  const completedRef = useRef(false);
  const requestActiveRef = useRef(false);
  const hasSeenLoadingRef = useRef(false);
  const [ensureAll] = useServiceChecker();

  //states, variables and logics for Source account (starts) ---------------------------------
  const [refreshAnimationId, setRefreshAnimationId] = useState(null);
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [previousRefreshData, setPreviousRefreshData] = useState(null);
  const [internalBalanceRefresh, setinternalBalanceRefresh] = useState(false);

  const services = useSelector((state) => state.refreshToken.services);
  const {
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshError,
    createTransactionBankRefreshData,
  } = useSelector((state) => state.ownerAllVerification);

  const cleanupAfterRefresh = () => {
    toastIdRef.current = null;
    requestActiveRef.current = false;
    completedRef.current = false;
    hasSeenLoadingRef.current = false;
  };

  useEffect(() => {
    // Track when loading starts to ensure we only process errors from actual API calls
    if (createTransactionBankRefreshLoading && requestActiveRef.current) {
      hasSeenLoadingRef.current = true;
      if (!toastIdRef.current) {
        UniversalRefreshToast();
      }
      return;
    }

    // Ignore anything after completion or if no request was made
    if (!requestActiveRef.current || completedRef.current) return;

    // Only process success/error if we've seen loading start (to avoid stale errors)
    if (!hasSeenLoadingRef.current) return;

    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshData?.status
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);

      CommonToast({
        text: "Balance refreshed successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
      });

      cleanupAfterRefresh();
      return;
    }

    // Only show error if a request was actually made and we've seen loading
    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshError &&
      requestActiveRef.current &&
      hasSeenLoadingRef.current
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);
      CommonToast({
        text: createTransactionBankRefreshError,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });

      cleanupAfterRefresh();
    }
  }, [
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshData,
    createTransactionBankRefreshError,
  ]);

  const handleRefresh = async (e, id, prevBalance, internalAccount) => {
    e.stopPropagation();

    if (
      !(services?.includes("VAccountBalance") || services?.includes("Admin"))
    ) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: ["VAccountBalance", "Admin"],
        },
      });
      return;
    }
    setinternalBalanceRefresh(true);

    if (requestActiveRef.current) return;

    requestActiveRef.current = true;
    completedRef.current = false;
    toastIdRef.current = null;

    setPreviousRefreshData(prevBalance);
    setRefreshAnimationId(id);

    let response = await dispatch(getCreateTransactionBalance(id));
    setinternalBalanceRefresh(false);
    if (response?.payload?.status === true) {
      if (internalAccount) {
        setTxnPayLoad((prev) => ({
          ...prev,
          destinationAcountOwnerLastUpdated:
            response.payload?.owner_accounts?.[0]
              ?.owner_account_balance_last_fetched_at_in_words,
          destinationAccountBalance:
            response.payload?.owner_accounts?.[0]?.owner_account_balance,
        }));
      } else {
        setTxnPayLoad((prev) => ({
          ...prev,
          ownerBankBalance:
            response.payload?.owner_accounts?.[0]?.owner_account_balance,
          ownerLastUpdated:
            response.payload?.owner_accounts?.[0]
              ?.owner_account_balance_last_fetched_at_in_words,
        }));
      }
    }
  };

  //states, variables and logics  for Source account (Ends) ---------------------------------

  const handleOpenModal = () => {
    if (!isEditDisabled) {
      if (page !== "confirm") {
        if (type === "destination") {
          setActiveType?.("pan");
          setRemPan?.("");
          setRemAccount?.("");
          setRemIFSC?.("");
        }
        setModalOpen(true);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAssociateOpenModal = () => {
    setActiveType?.("pan");
    setRemPan?.(txnPayLoad?.pan);
    setRemAccount?.("");
    setRemIFSC?.("");
    setAssociateModalOpen(true);
  };

  const handleAssociateCloseModal = () => {
    setAssociateModalOpen(false);
  };

  const registerNewAccount = () => {
    if (isFieldforce) {
      CommonToast({
        text: "Feature coming soon",
        icon: ICONS?.INFO,
        variant: "var(--coming-soon)",
        buttonNeeded: false,
      });
    } else {
      navigate("/beneficiary_account_dashboard/create_beneficiary_account");
    }
  };

  const hasBUAccountService = services?.includes("BUAccount");

  const service =
    ensureAll(["VAccountBalance"]).status || ensureAll(["Admin"]).status;
  return (
    <>
      {modalOpen && (type === "destination" || flow === "internal") && (
        <div className="fixed top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-light-opacity">
          <CommonAccountModal
            onClose={handleCloseModal}
            sourceDestiAcc={flow === "beneficiary" ? true : false}
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            flow={flow === "beneficiary" ? "beneTxn" : "internalTxn"}
            flowType={flowType}
            activeType={activeType}
            setActiveType={setActiveType}
            setRemPan={setRemPan}
            setRemAccount={setRemAccount}
            setRemIFSC={setRemIFSC}
            remIFSC={remIFSC}
            remAccount={remAccount}
            remPan={remPan}
            TypeRTGS={TypeRTGS}
            currentAccountDetails={currentAccountDetails}
          />
        </div>
      )}

      {modalOpen && type === "source" && (
        <div className="fixed top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            type={"source"}
            setOpenOwner={setModalOpen}
            TypeRTGS={TypeRTGS}
            currentAccountDetails={currentAccountDetails}
          />
        </div>
      )}
      {associateModalOpen && (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <CommonAccountModal
            sourceDestiAcc={false}
            onClose={handleAssociateCloseModal}
            onAccountSelect={handleOnAccountSelection}
            accountNumber={txnPayLoad?.bankAccountNo}
            IFSC={txnPayLoad?.bankAccountIFSC}
            panNumber={txnPayLoad?.pan}
            setTxnPayLoad={setTxnPayLoad}
            txnPayLoad={txnPayLoad}
            BeneTxnAssocAcct={true}
            setAssociateModalOpen={setAssociateModalOpen}
            flow={flow === "internal" ? "" : "beneTxn"}
            accountType={flow === "internal" ?? "internal"}
            associateTxn={flow === "internal" ?? "associateTxn"}
            ispanbankRequired={true}
            activeType={activeType}
            setActiveType={setActiveType}
            setRemPan={setRemPan}
            setRemAccount={setRemAccount}
            setRemIFSC={setRemIFSC}
            remIFSC={remIFSC}
            remAccount={remAccount}
            remPan={remPan}
            associateModalOpen={associateModalOpen}
          />
        </div>
      )}

      <div className="w-width-full">
        <div
          className={`flex flex-col relative rounded-border-radius-md ${shouldShowAssociatedAccounts && "gap-gap-md"}`}
        >
          <div
            className={`bg-primary-nav flex flex-col relative rounded-border-radius-md ${isDisabled ? "opacity-light-opacity cursor-not-allowed pointer-events-none" : ""}`}
          >
            <div className="p-padding-md h-height-full bg-primary-card flex flex-col rounded-border-radius-md">
              {page !== "confirm" &&
              ((txnPayLoad?.bankAccountNo &&
                txnPayLoad?.bankAccountIFSC &&
                type !== "source") ||
                (txnPayLoad?.ownerBankAccountNo &&
                  txnPayLoad?.ownerBankIFSC &&
                  type === "source")) ? (
                <div className="top-top-base right-right-base absolute">
                  <IconTearDropButton
                    handleFunction={handleOpenModal}
                    isDisabled={isEditDisabled || false}
                    isLoading={false}
                    enabledBgColor={"var(--primary-nav-act)"}
                    icon={ICONS?.EDIT}
                    hoverOption="white"
                    title={globalText?.EDIT}
                  />
                </div>
              ) : null}
              <div
                className={`flex flex-col gap-gap-md ${(txnPayLoad?.bankAccountNo && txnPayLoad?.bankAccountIFSC && type !== "source") || (txnPayLoad?.ownerBankAccountNo && txnPayLoad?.ownerBankIFSC && type === "source") ? " mb-margin-xl" : ""}`}
              >
                <div className="flex flex-col gap-gap-xs">
                  <span className="text-font-md text-text-primary font-semibold">
                    {accountHeader ||
                      (type === "source"
                        ? globalText?.SOURCE_ACCOUNT_CAPITALIZE
                        : globalText?.DESTINATION_ACCOUNT_CAPITALIZE)}
                  </span>
                  <span className="text-xs text-text-secondary">
                    {accountSubHeader ||
                      (type === "source"
                        ? globalText?.SOURCE_ACCOUNT_DES_TEXT
                        : globalText?.DESTINATION_ACCOUNT_DES_TEXT)}
                  </span>
                </div>

                {(!txnPayLoad?.bankAccountNo &&
                  !txnPayLoad?.bankAccountIFSC &&
                  type !== "source") ||
                (!txnPayLoad?.ownerBankAccountNo &&
                  !txnPayLoad?.ownerBankIFSC &&
                  type === "source") ? (
                  <div>
                    <PrimaryWithIconButton
                      btnText={
                        isReimbursement
                          ? globalText?.SELECT_REIMBURSEMENT_ACCOUNT
                          : type === "source"
                            ? globalText?.SELECT_OWNER_ACCOUNT
                            : globalText?.SELECT_DESTINATION_ACCOUNT
                      }
                      enabledBgColor="var(--primary-nav-act)"
                      isDisabled={false}
                      handleFunction={handleOpenModal}
                      iconToBeAlign="left"
                      enabledTextColor={"var(--text-primary)"}
                      icon={ICONS?.SELECT_ACCOUNT}
                      fullWidthRequired={true}
                      hoverOption="white"
                    />
                    {type === "destination" &&
                      flow === "beneficiary" &&
                      !txnPayLoad?.bankAccountNo &&
                      !txnPayLoad?.bankAccountIFSC && (
                        <div className="mt-margin-md">
                          <PrimaryWithIconButton
                            btnText={globalText?.REGISTER_NEW_ACCOUNT}
                            enabledBgColor="var(--primary-nav-act)"
                            handleFunction={registerNewAccount}
                            isDisabled={!hasBUAccountService}
                            iconToBeAlign="left"
                            enabledTextColor="var(--text-primary)"
                            icon={ICONS?.ADD_BUTTON_WHITE}
                            imgColor="var(--text-primary)"
                            fullWidthRequired
                            hoverOption="white"
                          />
                        </div>
                      )}
                  </div>
                ) : (txnPayLoad?.bankAccountNo &&
                    txnPayLoad?.bankAccountIFSC &&
                    type !== "source") ||
                  (txnPayLoad?.ownerBankAccountNo &&
                    txnPayLoad?.ownerBankIFSC &&
                    type === "source") ? (
                  <>
                    <div className="flex flex-col gap-gap-md">
                      <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                        <span className="text-sm text-text-primary font-semibold">
                          {type === "source"
                            ? formatAccountNumber(
                                txnPayLoad?.ownerBankAccountNo,
                              )
                            : formatAccountNumber(txnPayLoad?.bankAccountNo)}
                        </span>
                        <span className="text-xs text-text-secondary">
                          {globalText?.ACCOUNT_NUMBER}
                        </span>
                      </div>
                      {type === "destination" && flow === "internal" && (
                        <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                          <div className="text-sm text-text-primary font-semibold">
                            <div className="flex-1 truncate flex">
                              <TickerAnimation
                                text={
                                  txnPayLoad?.bankAccountName ||
                                  txnPayLoad?.destinationAccountName
                                }
                              />
                            </div>
                          </div>
                          <span className="text-xs text-text-secondary">
                            {globalText?.ACCOUNT_NAME}
                          </span>
                        </div>
                      )}
                      {type !== "source" && flow !== "internal" ? (
                        <div className="flex flex-col w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md gap-gap-xs">
                          <div className="text-sm text-text-primary font-semibold">
                            <div className="flex-1 truncate flex">
                              <TickerAnimation
                                text={txnPayLoad?.bankAccountName}
                              />
                            </div>
                          </div>
                          <span className="text-xs text-text-secondary">
                            {globalText?.ACCOUNT_NAME}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col Qlg:flex-row w-width-full h-auto py-padding-sm px-padding-md bg-primary-nav rounded-border-radius-md Qlg:gap-gap-xs gap-gap-base justify-between">
                          <div className="flex flex-col Qlg:flex-row flex-1 Qlg:gap-gap-sm gap-gap-md w-width-full Qlg:w-width-md">
                            {flow === "internal" ? (
                              <div className="w-width-full relative flex justify-between">
                                <div className="flex flex-col gap-gap-xs">
                                  <div className="flex items-center gap-gap-sm relative">
                                    <div
                                      style={{
                                        color:
                                          txnPayLoad?.destinationAccountBalance
                                            ? Number(
                                                txnPayLoad?.destinationAccountBalance,
                                              ) <
                                              GlobalConfiguration.lengthCheckZero
                                              ? "var(--failed)"
                                              : "var(--settled)"
                                            : "var(--text-primary)",
                                      }}
                                      className="text-left font-semibold flex text-sm"
                                    >
                                      {txnPayLoad?.destinationAccountBalance
                                        ? `
                                  ${shouldBeRedacted(txnPayLoad.destinationAccountBalance) ? "" : "INR "}
                              ${
                                formatTotalBalance(
                                  Number(txnPayLoad?.destinationAccountBalance),
                                  "Default",
                                ).amount
                              }
                              ${formatTotalBalance(Number(txnPayLoad?.destinationAccountBalance), "Default").abbr}
                          `
                                        : globalText?.NOT_AVAILABLE}
                                    </div>
                                    <div className={"loader_and_arrow "}>
                                      {internalBalanceRefresh ? (
                                        <Loader
                                          height="var(--icon-sm)"
                                          width="var(--icon-sm)"
                                        />
                                      ) : (
                                        <div className="relative h-icon-sm">
                                          {!createTransactionBankRefreshLoading &&
                                            txnPayLoad?.destinationAcountOwnerId ===
                                              refreshAnimationId &&
                                            Number(
                                              txnPayLoad?.destinationAccountBalance,
                                            ) > Number(previousRefreshData) && (
                                              <SVGComponent
                                                url={ICONS.ARROW_UP_GREEN_GIF}
                                              />
                                            )}

                                          {!createTransactionBankRefreshLoading &&
                                            txnPayLoad?.destinationAcountOwnerId ===
                                              refreshAnimationId &&
                                            Number(
                                              txnPayLoad?.destinationAccountBalance,
                                            ) < Number(previousRefreshData) && (
                                              <SVGComponent
                                                url={ICONS.ARROW_DOWN_RED_GIF}
                                              />
                                            )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-left text-xs text-text-secondary whitespace-nowrap">
                                    <div className="w-full max-w-full overflow-hidden">
                                      <TickerAnimation
                                        text={
                                          txnPayLoad?.isDestinationOwnerActive
                                            ? `${
                                                txnPayLoad?.destinationAccountBalance
                                                  ? formatTotalBalance(
                                                      txnPayLoad?.destinationAccountBalance,
                                                      "Default",
                                                    ).prefix === "crores"
                                                    ? `Balance in ${
                                                        formatTotalBalance(
                                                          txnPayLoad?.destinationAccountBalance,
                                                          "Default",
                                                        ).prefix
                                                      } - `
                                                    : globalText?.BALANCE_UPDATED
                                                  : globalText?.BALANCE_UPDATED
                                              } ${txnPayLoad?.destinationAcountOwnerLastUpdated}`
                                            : "Balance"
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                                {txnPayLoad?.isDestinationOwnerActive
                                  ? service && (
                                      <>
                                        <div className="flex flex-col justify-center">
                                          <CardActionableButton
                                            btnType="button"
                                            bgColor="var(--primary-nav-act)"
                                            handleFunction={(e) => {
                                              handleRefresh(
                                                e,
                                                txnPayLoad?.destinationAcountOwnerId,
                                                txnPayLoad?.destinationAccountBalance,
                                                true,
                                              );
                                            }}
                                            isDisabled={
                                              ensureAll(["VAccountBalance"])
                                                .status ||
                                              ensureAll(["Admin"]).status
                                                ? false
                                                : true
                                            }
                                            disabledBgColor="var(--primary-nav-act)"
                                            icon={ICONS?.RELOAD_REFRESH_WHITE}
                                            hoverOption="white"
                                            title="Refresh"
                                          />
                                        </div>
                                      </>
                                    )
                                  : null}
                              </div>
                            ) : (
                              <div className="w-width-full flex justify-between">
                                <div className="flex flex-col gap-gap-xs">
                                  <div className="flex items-center gap-gap-sm relative">
                                    <div
                                      className={`text-left  text-sm 
                                     ${
                                       txnPayLoad?.ownerBankBalance
                                         ? txnPayLoad?.ownerBankBalance <
                                             GlobalConfiguration?.lengthCheckZero &&
                                           !(
                                             isNaN(
                                               txnPayLoad?.ownerBankBalance,
                                             ) ||
                                             txnPayLoad?.ownerBankBalance ===
                                               "xxxx.xx" ||
                                             txnPayLoad?.ownerBankBalance ===
                                               null ||
                                             txnPayLoad?.ownerBankBalance ===
                                               undefined
                                           )
                                           ? "text-failed font-semibold"
                                           : "text-settled font-semibold"
                                         : "text-text-primary font-normal"
                                     }
                                    `}
                                    >
                                      {shouldBeRedacted(
                                        txnPayLoad?.ownerBankBalance,
                                      )
                                        ? ""
                                        : "INR "}
                                      {(txnPayLoad?.ownerBankBalance ??
                                      formatTotalBalance(
                                        txnPayLoad?.ownerBankBalance,
                                        "Default",
                                      ).prefix === "crores")
                                        ? formatTotalBalance(
                                            txnPayLoad?.ownerBankBalance,
                                            "crores",
                                          ).amount
                                        : formatTotalBalance(
                                            txnPayLoad?.ownerBankBalance,
                                            "Default",
                                          ).amount}
                                    </div>

                                    {service && (
                                      <>
                                        {createTransactionBankRefreshLoading &&
                                        internalBalanceRefresh ? (
                                          <Loader
                                            height="var(--icon-sm)"
                                            width="var(--icon-sm)"
                                          />
                                        ) : (
                                          <div className="relative">
                                            {!createTransactionBankRefreshLoading &&
                                              txnPayLoad?.ownerId ===
                                                refreshAnimationId &&
                                              Number(
                                                txnPayLoad?.ownerBankBalance,
                                              ) <
                                                Number(previousRefreshData) && (
                                                <SVGComponent
                                                  url={
                                                    ICONS?.ARROW_DOWN_RED_GIF
                                                  }
                                                  height="var(--icon-sm)"
                                                />
                                              )}

                                            {!createTransactionBankRefreshLoading &&
                                              txnPayLoad?.ownerId ===
                                                refreshAnimationId &&
                                              Number(
                                                txnPayLoad?.ownerBankBalance,
                                              ) >
                                                Number(previousRefreshData) && (
                                                <SVGComponent
                                                  url={
                                                    ICONS?.ARROW_UP_GREEN_GIF
                                                  }
                                                  height="var(--icon-sm)"
                                                />
                                              )}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="text-left text-xs text-text-secondary whitespace-nowrap">
                                    <div className="w-full max-w-full overflow-hidden">
                                      <TickerAnimation
                                        text={`${
                                          txnPayLoad?.ownerBankBalance
                                            ? formatTotalBalance(
                                                txnPayLoad?.ownerBankBalance,
                                                "Default",
                                              ).prefix === "crores"
                                              ? `Balance in ${
                                                  formatTotalBalance(
                                                    txnPayLoad?.ownerBankBalance,
                                                    "Default",
                                                  ).prefix
                                                } - `
                                              : globalText?.BALANCE_UPDATED
                                            : globalText?.BALANCE_UPDATED
                                        } ${txnPayLoad?.ownerLastUpdated}`}
                                      />
                                    </div>
                                  </div>
                                </div>
                                {service && (
                                  <>
                                    {
                                      <div className="flex flex-col justify-center">
                                        <CardActionableButton
                                          btnType="button"
                                          handleFunction={(e) => {
                                            handleRefresh(
                                              e,
                                              txnPayLoad?.ownerId,
                                              txnPayLoad?.ownerBankBalance,
                                            );
                                          }}
                                          isDisabled={
                                            ensureAll(["VAccountBalance"])
                                              .status ||
                                            ensureAll(["Admin"]).status
                                              ? false
                                              : true
                                          }
                                          disabledBgColor="var(--primary-nav-act)"
                                          icon={ICONS?.RELOAD_REFRESH_WHITE}
                                          hoverOption="white"
                                          title="Refresh"
                                          bgColor={"var(--primary-nav-act)"}
                                        />
                                      </div>
                                    }
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between absolute right-right-base bottom-bottom-base">
                      <div className="bg-text-primary rounded-tl-border-radius-md rounded-br-border-radius-md flex items-center justify-around w-width-full h-full py-padding-sm px-padding-md">
                        <CommonBankLogo
                          ifsc={
                            type === "destination"
                              ? txnPayLoad?.bankAccountIFSC
                              : txnPayLoad?.ownerBankIFSC
                          }
                          variant="big"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
          <div>
            <div className="w-width-full flex flex-col gap-gap-md">
              {shouldShowAssociatedAccounts &&
                type === "destination" &&
                (flow === "beneficiary" || flow === "internal") && (
                  <CommonAlertCard
                    title={globalText?.ACTIVE_ASSOCIATED_ACCOUNTS}
                    texts={
                      flow === "beneficiary"
                        ? globalText?.OTHER_EXISTING_BENEFICIARY_ACCOUNTS_FOUND
                        : globalText?.OTHER_EXISTING_INTERNAL_ACCOUNTS_FOUND
                    }
                    onClick={handleAssociateOpenModal}
                    icon={""}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SourceDestinationAccountDetail;
